import { ePlanCode, ePlanFrequency, eSubscriptionStatus } from './Subscription.enums';
import type { tSubscriptionSummary } from './Subscription.types';
import type { tPlan } from '../../pages/Settings/components/SubscriptionsManager/SubscriptionManager.types';

/**
 * Single source of truth for the "Pro or Advanced (Enterprise) plan" predicate
 * shared by every plan-gated surface (Smart Leadbot, WAPI banner, Lead Agent
 * acceptance banner, advisor help, …). A null/undefined planCode — the loading
 * default before the subscription summary resolves — fails closed.
 */
export function isProOrAdvancedPlan(planCode: ePlanCode | null | undefined): boolean {
  return planCode === ePlanCode.PLAN_PROFESSIONAL || planCode === ePlanCode.PLAN_ENTERPRISE;
}

/**
 * Returns true for a "converted" Pro/Advanced subscriber — Pro/Advanced AND past
 * the trial (`status !== TRIAL`). The Lead Agent paywall/acceptance surfaces
 * (C2/C6/C7/C8) are converts-only: a subscription still in its trial has not paid
 * for the base plan, so it must never see the offer (RET-1525). Mirrors the
 * workspace-service guard that withholds the acceptance row for trials.
 *
 * Excludes ONLY the TRIAL status (deliberate, JC 2026-06-29) — any other
 * non-active state (PAST_DUE, etc.) has already paid at least once and stays
 * eligible. A null/undefined subscription fails closed (no banner) until the
 * subscription summary resolves. Note: this does NOT consult `isInitialTrial`
 * (see `isLeadAgentTrialLocked`) — every trial is excluded from the paywall,
 * including returning-customer trial extensions.
 */
export function isConvertedProOrAdvanced(
  subscription: Pick<tSubscriptionSummary, 'planCode' | 'status'> | null | undefined,
): boolean {
  if (!subscription) {
    return false;
  }

  return isProOrAdvancedPlan(subscription.planCode) && subscription.status !== eSubscriptionStatus.TRIAL;
}

/**
 * Returns true when the workspace subscription allows customizing the agent name.
 * Available to trial, Professional, and Advanced (Enterprise) plans.
 */
export function canCustomizeAgentName(subscription: Pick<tSubscriptionSummary, 'status' | 'planCode'>): boolean {
  return (
    subscription.status === (eSubscriptionStatus.TRIAL as string) ||
    isProOrAdvancedPlan(subscription.planCode)
  );
}

/**
 * Returns true when the workspace subscription is eligible to use the Smart
 * Leadbot (Lead Agent). Restricted to Professional and Advanced (Enterprise)
 * plans during the free-period release; ineligible plans see the SLB card
 * disabled with an upsell tooltip.
 */
export function canAccessSmartLeadbot(subscription: Pick<tSubscriptionSummary, 'planCode'>): boolean {
  return isProOrAdvancedPlan(subscription.planCode);
}

/**
 * Returns true when the Lead Agent must stay locked during a subscription trial.
 *
 * Only the workspace's INITIAL trial blocks activation: a genuine first-time signup
 * (trialing with no prior paid subscription) may configure and sandbox the Lead Agent
 * but may NOT activate it on a real channel until it converts to a paid subscription.
 * A returning customer or a staff-granted trial extension — which carries a prior paid
 * subscription — is NOT the opportunistic-signup abuse case the lock targets, so it is
 * not blocked. The `isInitialTrial` billing fact is computed once in leadbilling and
 * surfaced through bff-web; the frontend does not re-derive it.
 *
 * Only a trial can ever be locked — an active/paid subscription is never blocked, even
 * if the `isInitialTrial` flag arrives stale-true. Within a trial: a present flag is
 * trusted (initial => lock, otherwise => allow), while an absent or null flag falls back
 * to locking any trial. The fallback covers the rollout window (frontend deployed ahead
 * of bff-web) and any source that sends JSON null instead of omitting the key —
 * over-locking briefly is safer than opening the abuse window. A null/undefined
 * subscription leaves the gate off until the subscription summary resolves.
 */
export function isLeadAgentTrialLocked(
  subscription: Pick<tSubscriptionSummary, 'status' | 'isInitialTrial'> | null | undefined,
): boolean {
  if (!subscription) {
    return false;
  }

  // Hard guard: the lock applies only during a trial, never to an active/paid subscriber.
  if (subscription.status !== eSubscriptionStatus.TRIAL) {
    return false;
  }

  // Within a trial, an absent or null billing fact falls back to locking (rollout-safe).
  // This branch is dead once every subscription summary carries isInitialTrial.
  if (subscription.isInitialTrial === undefined || subscription.isInitialTrial === null) {
    return true;
  }

  return subscription.isInitialTrial === true;
}

/**
 * Returns true only when the subscription is explicitly in its trial.
 *
 * Deliberately fails OPEN (false) for a null/undefined subscription and for the
 * `INACTIVE` pre-load default: callers use this to withhold paid actions, and
 * blocking a paying customer because the summary hasn't resolved yet is far
 * worse than briefly offering an action the backend will reject with its own
 * explanatory error (BUG-775).
 */
export function isTrialSubscription(
  subscription: Pick<tSubscriptionSummary, 'status'> | null | undefined,
): boolean {
  return subscription?.status === (eSubscriptionStatus.TRIAL as string);
}

/**
 * Decides whether the "¿Deseas continuar con tu suscripción?" trial confirmation
 * modal should show, given the subscription status and the count of unread
 * prompt_trial_confirmation notifications.
 *
 * - PAUSED: trial expired with no notification created — always prompt.
 * - ACTIVE: already paid — NEVER prompt, even if a stale unread notification
 *   lingers (RET-1506: trials that auto-converted to ACTIVE left the notification
 *   unread, so paid customers saw the modal forever).
 * - Otherwise (TRIAL, etc.): prompt when there is at least one unread notification.
 */
export function shouldShowTrialConfirmationModal(
  status: eSubscriptionStatus | string | null | undefined,
  unreadTrialNotificationCount: number,
): boolean {
  if (status === eSubscriptionStatus.PAUSED) return true;
  if (status === eSubscriptionStatus.ACTIVE) return false;
  return unreadTrialNotificationCount >= 1;
}

/**
 * Returns true when the workspace is on a plan that includes WhatsApp Business
 * API access (Professional or Advanced). Basic and Legacy plans connect via
 * WhatsApp Web and must not see any WhatsApp API connection alert — neither
 * the disconnection banner nor the never-connected nudge.
 */
export function canSeeWapiDisconnectedBanner(subscription: Pick<tSubscriptionSummary, 'planCode'>): boolean {
  return isProOrAdvancedPlan(subscription.planCode);
}

/**
 * Returns true when the workspace subscription is eligible to see the WhatsApp
 * API advisor help banner (live session / schedule with an advisor) inside the
 * dashboard's "Conectar WhatsApp API" modal. Available to trial, Professional,
 * and Advanced (Enterprise) plans — same gate used by other advisor-facing
 * surfaces. A null/undefined subscription is treated as ineligible so the
 * banner stays hidden until subscription context resolves.
 */
export function isAdvisorHelpEligible(
  subscription: Pick<tSubscriptionSummary, 'status' | 'planCode'> | null | undefined,
): boolean {
  if (!subscription) {
    return false;
  }

  return (
    subscription.status === (eSubscriptionStatus.TRIAL as string) ||
    isProOrAdvancedPlan(subscription.planCode)
  );
}

/**
 * Maps the per-period results of the parallel `getAvailablePlans` calls — one
 * `Promise.allSettled` entry per `ePeriod`, in `ePeriod` order — into the plans
 * record keyed by frequency.
 *
 * RET-1190: a rejected period contributes an empty list AND flags
 * `hasFailedFetch` so the caller can surface a retry toast. A fulfilled-but-empty
 * period is NOT a failure (it just has no plans). Each fulfilled period keeps
 * only the plans whose `frequencyName` matches that period.
 */
export function buildPlanTiersFromSettled(
  settledResponses: PromiseSettledResult<tPlan[]>[],
): { plans: Record<ePlanFrequency, tPlan[]>; hasFailedFetch: boolean } {
  const frequencies = Object.values(ePlanFrequency);
  let hasFailedFetch = false;

  const plans = settledResponses.reduce(
    (acc, response, index) => {
      const period = frequencies[index];
      if (response.status === 'fulfilled') {
        acc[period] = response.value.filter(
          (plan) => plan.frequencyName === period,
        );
      } else {
        hasFailedFetch = true;
        acc[period] = [];
      }
      return acc;
    },
    {} as Record<ePlanFrequency, tPlan[]>,
  );

  return { plans, hasFailedFetch };
}
